import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { CSSTransition, TransitionGroup } from "react-transition-group";

import styles from "./phoneme.module.css";
import { State } from "../../redux/store";
import * as phonemeActions from "../../redux/actions/phoneme";
import { srcPath } from "../../util/Path";
import PhonicsCard from "./PhonicsCard";

const cloudStyle = (index: number): any => {
  let top: number, left: number;
  switch (index) {
    case 0:
      top = -20;
      left = 140;
      break;
    case 1:
      top = 250;
      left = -300;
      break;
    case 2:
      top = 360;
      left = 280;
      break;
  }
  return { top: `${top}px`, left: `calc(50% + ${left - 200}px)` };
};

const CORRECT_CLOUD_DIFF = 123;

interface CloudProps {
  phonics: string;
  index: number;
}

const Cloud = (props: CloudProps) => {
  const { phonics, index } = props;
  const dispatch = useDispatch();

  const {
    cloudPlaying,
    cloudPlayIndex,
    cloudCovered,
    cloudDisplay,
    cloudAnswer,
    cloudRound,
    cloudClickable,
  } = useSelector((state: State) => state.phoneme);

  const isPlaying = cloudPlayIndex === index && cloudPlaying;
  const isCovered = !!cloudCovered[index];
  const isAnswer = cloudDisplay[index] === cloudAnswer;
  const isCorrectImg = !isCovered && isAnswer;

  let cloudImg = "cloud.png";
  if (!isCovered) {
    cloudImg = isAnswer ? "correct_cloud.png" : "wrong_cloud.png";
  }

  const cloudClick = () => {
    if (!isCovered || !cloudClickable) return;
    dispatch(phonemeActions.clickCloud(index));
  };

  return (
    <CSSTransition
      in={isPlaying && isCovered}
      timeout={500}
      classNames={{
        enter: styles.cloudEnter,
        enterActive: styles.cloudEnterActive,
        enterDone: styles.cloudEnterDone,
        exit: styles.cloudExit,
        exitActive: styles.cloudExitActive,
      }}
    >
      <div
        className={styles.cloudContainer}
        style={{
          ...cloudStyle(index),
          cursor: isCovered && cloudClickable ? "pointer" : "default",
        }}
        onClick={() => cloudClick()}
      >
        <TransitionGroup>
          <CSSTransition
            key={!isCovered || cloudRound}
            appear
            timeout={1000}
            classNames={{
              appear: styles.cloudImgEnter,
              appearActive: styles.cloudImgEnterActive,
              enter: styles.cloudImgEnter,
              enterActive: styles.cloudImgEnterActive,
              exit: styles.cloudImgExit,
              exitActive: styles.cloudImgExitActive,
            }}
          >
            <div
              className={styles.cloud}
              style={{
                top: isCorrectImg ? `-${CORRECT_CLOUD_DIFF}px` : 0,
              }}
            >
              <img
                className={styles.cloudImg}
                src={srcPath(`phoneme/clouds/${cloudImg}`)}
                alt=""
              />
              <div
                className={styles.cloudPhonicsContainer}
                style={{
                  top: isCorrectImg ? `${CORRECT_CLOUD_DIFF}px` : 0,
                  height: !isCovered && !isAnswer ? "243px" : "223px",
                  visibility: isCovered ? "hidden" : "visible",
                }}
              >
                <img
                  className={styles.cloudPhonicsImg}
                  src={srcPath(`phoneme/phonics_icons/icon_${phonics}.png`)}
                  alt=""
                />
                <PhonicsCard
                  phonics={phonics}
                  style={{
                    transform: "scale(1.3)",
                    marginTop: "30px",
                  }}
                />
              </div>
            </div>
          </CSSTransition>
        </TransitionGroup>
      </div>
    </CSSTransition>
  );
};

const CloudPage = () => {
  const dispatch = useDispatch();
  const {
    cloudRound,
    cloudTotalRound,
    cloudDisplay,
    cloudCovered,
    cloudPlayNext,
    cloudAnswer,
    cloudReadyEnd,
    instruction,
  } = useSelector((state: State) => state.phoneme);
  const hiddenBtn = useRef<HTMLButtonElement>();

  const btnHandler = () => {
    const index = cloudDisplay.indexOf(cloudAnswer);
    if (cloudCovered[index]) {
      dispatch(phonemeActions.cloudPlay());
    } else {
      if (cloudRound >= cloudTotalRound) {
        setTimeout(() => dispatch(phonemeActions.endCloud()), 1000);
      } else {
        setTimeout(() => dispatch(phonemeActions.cloudSetRound()), 200);
      }
    }
  };

  useEffect(() => {
    if (!instruction) {
      dispatch(phonemeActions.cloudSetRound());
    }
  }, [dispatch, instruction]);

  useEffect(() => {
    if (cloudPlayNext) {
      hiddenBtn.current.click();
    }
  }, [dispatch, cloudPlayNext]);

  return (
    <>
      <div
        className={styles.cloudPage}
        style={{
          opacity: cloudReadyEnd ? 0 : 1,
        }}
      >
        {cloudDisplay.map((cloud, index) => (
          <Cloud key={index} phonics={cloud} index={index} />
        ))}
        <button
          style={{ visibility: "hidden" }}
          ref={hiddenBtn}
          type="button"
          onClick={() => btnHandler()}
        ></button>
      </div>
    </>
  );
};

export default CloudPage;
